import { env } from '@/core/config/env'
import type { AccountRepository } from '@/domain/repositories/account.repository'
import type { AddonGroupRepository } from '@/domain/repositories/addon-group.repository'
import type { AuthRepository } from '@/domain/repositories/auth.repository'
import type { CategoryRepository } from '@/domain/repositories/category.repository'
import type { DashboardRepository } from '@/domain/repositories/dashboard.repository'
import type { DiscountRepository } from '@/domain/repositories/discount.repository'
import type { ModifierRepository } from '@/domain/repositories/modifier.repository'
import type { OutletRepository } from '@/domain/repositories/outlet.repository'
import type { PaymentMethodRepository } from '@/domain/repositories/payment-method.repository'
import type { PermissionRepository } from '@/domain/repositories/permission.repository'
import type { ProductRepository } from '@/domain/repositories/product.repository'
import type { PriceTypeRepository } from '@/domain/repositories/price-type.repository'
import type { RoleRepository } from '@/domain/repositories/role.repository'
import type { TableLayoutRepository } from '@/domain/repositories/table-layout.repository'
import type { TransactionRepository } from '@/domain/repositories/transaction.repository'
import type { UnitRepository } from '@/domain/repositories/unit.repository'
import type { IngredientRepository } from '@/domain/repositories/ingredient.repository'
import type { RecipeRepository } from '@/domain/repositories/recipe.repository'
import type { ProductionRepository } from '@/domain/repositories/production.repository'
import type { StockTransferRepository } from '@/domain/repositories/stock-transfer.repository'
import { AuthHttpRepository } from '@/infrastructure/auth/auth.http.repository'
import { AccountHttpRepository } from '@/infrastructure/access/account.http.repository'
import { OutletHttpRepository } from '@/infrastructure/access/outlet.http.repository'
import { PermissionHttpRepository } from '@/infrastructure/access/permission.http.repository'
import { RoleHttpRepository } from '@/infrastructure/access/role.http.repository'
import { AddonGroupHttpRepository } from '@/infrastructure/catalog/addon-group.http.repository'
import { CategoryHttpRepository } from '@/infrastructure/catalog/category.http.repository'
import { ModifierHttpRepository } from '@/infrastructure/catalog/modifier.http.repository'
import { PriceTypeHttpRepository } from '@/infrastructure/catalog/price-type.http.repository'
import { ProductHttpRepository } from '@/infrastructure/catalog/product.http.repository'
import { DashboardHttpRepository } from '@/infrastructure/dashboard/dashboard.http.repository'
import { DiscountHttpRepository } from '@/infrastructure/discount/discount.http.repository'
import { PaymentMethodHttpRepository } from '@/infrastructure/payment/payment-method.http.repository'
import { TableLayoutHttpRepository } from '@/infrastructure/table/table-layout.http.repository'
import { TransactionHttpRepository } from '@/infrastructure/transaction/transaction.http.repository'
import { UnitHttpRepository } from '@/infrastructure/inventory/unit.http.repository'
import { IngredientHttpRepository } from '@/infrastructure/inventory/ingredient.http.repository'
import { RecipeHttpRepository } from '@/infrastructure/inventory/recipe.http.repository'
import { ProductionHttpRepository } from '@/infrastructure/inventory/production.http.repository'
import { StockTransferHttpRepository } from '@/infrastructure/inventory/stock-transfer.http.repository'
import { PurchaseOrderHttpRepository } from '@/infrastructure/inventory/purchase-order.http.repository'
import type { PrinterRepository } from '@/domain/repositories/printer.repository'
import { PrinterHttpRepository } from '@/infrastructure/access/printer.http.repository'
import { PrinterMockRepository } from '@/infrastructure/access/printer.mock.repository'
import { TableLayoutMockRepository } from '@/infrastructure/table/table-layout.mock.repository'
import { DashboardMockRepository } from '@/infrastructure/dashboard/dashboard.mock.repository'

import type { InventoryReportRepository } from '@/domain/repositories/inventory-report.repository'

import { InventoryReportHttpRepository } from '@/infrastructure/inventory/inventory-report.http.repository'

import type { StockRepository } from '@/domain/repositories/stock.repository'
import { StockHttpRepository } from '@/infrastructure/inventory/stock.http.repository'

/**
 * Composition root sederhana. Di sinilah satu-satunya tempat memilih
 * implementasi konkret. Application/presentation layer cukup mengimpor
 * `container.*` (bertipe interface domain).
 */
function createContainer() {
  // Auth selalu ke BE nyata (butuh backend Go).
  const authRepository: AuthRepository = new AuthHttpRepository()

  // Dashboard: mock selama BE belum punya endpoint /dashboard/*.
  const dashboardRepository: DashboardRepository = env.useMockDashboard
    ? new DashboardMockRepository()
    : new DashboardHttpRepository()

  const outletRepository: OutletRepository = new OutletHttpRepository()

  const permissionRepository: PermissionRepository = new PermissionHttpRepository()

  const roleRepository: RoleRepository = new RoleHttpRepository()

  const accountRepository: AccountRepository = new AccountHttpRepository()

  const categoryRepository: CategoryRepository = new CategoryHttpRepository()

  const priceTypeRepository: PriceTypeRepository = new PriceTypeHttpRepository()

  const modifierRepository: ModifierRepository = new ModifierHttpRepository()

  const productRepository: ProductRepository = new ProductHttpRepository()

  const addonGroupRepository: AddonGroupRepository = new AddonGroupHttpRepository()

  const paymentMethodRepository: PaymentMethodRepository = new PaymentMethodHttpRepository()

  const discountRepository: DiscountRepository = new DiscountHttpRepository()

  const transactionRepository: TransactionRepository = new TransactionHttpRepository()

  const unitRepository: UnitRepository = new UnitHttpRepository()
  const ingredientRepository: IngredientRepository = new IngredientHttpRepository()
  const recipeRepository: RecipeRepository = new RecipeHttpRepository()
  const productionRepository: ProductionRepository = new ProductionHttpRepository()
  const stockTransferRepository: StockTransferRepository = new StockTransferHttpRepository()
  const purchaseOrderRepository = new PurchaseOrderHttpRepository()
  const stockRepository: StockRepository = new StockHttpRepository()
  const inventoryReportRepository: InventoryReportRepository = new InventoryReportHttpRepository()

  // Layout meja: mock selama BE belum punya endpoint /table-layout.
  const tableLayoutRepository: TableLayoutRepository = env.useMockTables
    ? new TableLayoutMockRepository()
    : new TableLayoutHttpRepository()

  const printerRepository: PrinterRepository = env.useMockPrinters
    ? new PrinterMockRepository()
    : new PrinterHttpRepository()

  return {
    authRepository,
    dashboardRepository,
    outletRepository,
    permissionRepository,
    roleRepository,
    accountRepository,
    categoryRepository,
    priceTypeRepository,
    modifierRepository,
    productRepository,
    addonGroupRepository,
    paymentMethodRepository,
    discountRepository,
    transactionRepository,
    unitRepository,
    ingredientRepository,
    recipeRepository,
    productionRepository,
    stockTransferRepository,
    purchaseOrderRepository,
    stockRepository,
    inventoryReportRepository,
    tableLayoutRepository,
    printerRepository,
  }
}

export const container = createContainer()
